/**
 * OutletMetricCell - the single per-outlet stock/avg-cost cell used by every
 * product exception table (via `buildOutletCostColumns`). One component owns
 * the two-line layout, value formats, and anomaly rules so stock-focused and
 * price-focused reports can't drift apart:
 *
 *   stock-led cell:   ▼ -7          cost-led cell:   ⚠ $0.00
 *                     $252.52                        2
 *
 * Value/state rules (icon-free; salience order is red chip > quiet amber
 * cost > neutral values). No bold anywhere - the primary line is set
 * slightly larger than the secondary instead, so problem colours (red
 * chip, amber cost) are the only loud signals:
 * - Cost line is always `$`-prefixed. A $0.00 cost reads in the warning
 *   token - the color alone carries the anomaly. Suppressed entirely via
 *   `expectsCost={false}` on reports where zero cost is the correct state
 *   (non-inventory).
 * - Every inventory record's stock is contained in a soft chip, zero
 *   included - the chip's presence is the "inventory record exists here"
 *   signal:
 *     stock >= 0 -> neutral chip (theme action.hover fill)
 *     stock < 0  -> red chip (light brand-red tint + brand red text); the
 *                   minus sign carries the meaning, no glyph
 * - `null` (no inventory record) renders a muted dash + a small
 *   "not stocked" label on the stock line; a missing cost is a muted dash.
 * - `NUMERIC_FONT_SX` (Inter + tabular-nums, shared with the Compare Sales
 *   surface and the table engines) keeps digits aligned down the column.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { formatCurrency } from '../../../utils/inspectorFormatters'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'

export interface OutletMetricCellProps {
  /** On-hand stock at this outlet. `null` = no inventory record. */
  stock: number | null
  /** Average cost at this outlet. `null` = no cost record. */
  avgCost: number | null
  /** Which metric leads visually (matches the report's `outletMode`). */
  primary: 'cost' | 'stock'
  /** When true (default), a $0.00 avg cost is an anomaly. */
  expectsCost?: boolean
  /**
   * When false, omit the cost line entirely (the "Total"
   * column shows the stock chip alone - there is no aggregate avg cost).
   */
  showCost?: boolean
  /**
   * 'total' styles the stock chip as the cross-outlet rollup: bold and
   * one size step smaller than the primary outlet chips, so it reads as
   * a summary without outshouting the per-outlet diagnosis.
   */
  variant?: 'outlet' | 'total'
}

type LineRole = 'primary' | 'secondary'

// Brand red for the negative-stock chip. Matches the compare-sales
// mismatch vocabulary (#EC1C5A border / 8% tint) so "red means a real
// problem" reads the same on both surfaces.
const NEGATIVE_TEXT = '#EC1C5A'
const NEGATIVE_BG = 'rgba(236, 28, 90, 0.08)'

// Hierarchy by size, not weight: the primary line runs very slightly
// larger than the secondary; everything stays at normal weight. The
// primary stock chip gets one extra step (14px, below) so a stock-led
// number leads the cell.
const lineSx = (role: LineRole) => ({
  ...NUMERIC_FONT_SX,
  lineHeight: 1.25,
  fontSize: role === 'primary' ? '0.9rem' : '0.72rem',
  fontWeight: 400,
})

const MutedDash: React.FC<{ role: LineRole }> = ({ role }) => (
  <Typography component="span" sx={{ ...lineSx(role), color: 'text.disabled' }}>
    -
  </Typography>
)

const CostLine: React.FC<{
  avgCost: number | null
  role: LineRole
  expectsCost: boolean
}> = ({ avgCost, role, expectsCost }) => {
  if (avgCost === null) return <MutedDash role={role} />
  const anomaly = avgCost === 0 && expectsCost
  return (
    <Typography
      component="span"
      sx={{
        ...lineSx(role),
        // Warning token at normal weight - color alone signals the anomaly.
        color: anomaly
          ? 'warning.main'
          : role === 'primary'
            ? 'text.primary'
            : 'text.secondary',
      }}
    >
      {formatCurrency(avgCost)}
    </Typography>
  )
}

const StockLine: React.FC<{
  stock: number | null
  role: LineRole
  total?: boolean
}> = ({ stock, role, total = false }) => {
  if (stock === null) {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '0.6rem', color: 'text.disabled' }}
        >
          not stocked
        </Typography>
        <MutedDash role={role} />
      </Box>
    )
  }
  // Any inventory record - zero included - earns the chip; only a null
  // (no record) renders the "not stocked" dash above.
  const negative = stock < 0
  return (
    <Box
      component="span"
      sx={{
        ...NUMERIC_FONT_SX,
        display: 'inline-flex',
        justifyContent: 'flex-end',
        borderRadius: '6px',
        padding: '1px 8px',
        fontSize: total ? '13px' : role === 'primary' ? '14px' : '12px',
        lineHeight: 1.25,
        fontWeight: total ? 600 : 400,
        bgcolor: negative ? NEGATIVE_BG : 'action.hover',
        color: negative
          ? NEGATIVE_TEXT
          : role === 'primary'
            ? 'text.primary'
            : 'text.secondary',
      }}
    >
      {stock}
    </Box>
  )
}

const OutletMetricCell: React.FC<OutletMetricCellProps> = ({
  stock,
  avgCost,
  primary,
  expectsCost = true,
  showCost = true,
  variant = 'outlet',
}) => {
  const cost = showCost ? (
    <CostLine avgCost={avgCost} role={primary === 'cost' ? 'primary' : 'secondary'} expectsCost={expectsCost} />
  ) : null
  const stockNode = (
    <StockLine
      stock={stock}
      role={primary === 'stock' ? 'primary' : 'secondary'}
      total={variant === 'total'}
    />
  )
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '4px',
      }}
    >
      {primary === 'stock' ? stockNode : cost}
      {primary === 'stock' ? cost : stockNode}
    </Box>
  )
}

export default OutletMetricCell
